import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactECharts from "echarts-for-react";
import LoadingBlock from "../LoadingBlock/LoadingBlock";

function isDarkTheme() {
  if (typeof window === "undefined") return false;
  return localStorage.getItem("theme") === "dark";
}

function readCssVar(name, fallback) {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name);
  return (v || "").trim() || fallback;
}

export default function ProtagonismBySubjectSankeyChart({
  links = [],
  loading = false,
  title = "Top 10 - Protagonismo por assunto",
  filename = "protagonismo-por-assunto",
  exportMode = false,
}) {
  const dark = isDarkTheme();
  const chartRef = useRef(null);

  const CARD_MIN_H = 360;
  const CHART_H = 320;
  const MAX = 10;

  const [chartKey, setChartKey] = useState(0);
  useEffect(() => {
    const onEnd = () => setChartKey((k) => k + 1);
    window.addEventListener("dashboard-export:end", onEnd);
    return () => window.removeEventListener("dashboard-export:end", onEnd);
  }, []);

  const wrapStyle = useMemo(() => {
    if (!exportMode) {
      return {
        position: "relative",
        borderRadius: 12,
        padding: 12,
        border: "none",
        background: "var(--background-graphic)",
        boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
        overflow: "hidden",
        minHeight: CARD_MIN_H,
      };
    }

    return {
      position: "relative",
      borderRadius: 0,
      padding: 0,
      border: "none",
      background: "var(--background-graphic)",
      boxShadow: "none",
      overflow: "hidden",
      minHeight: 560,
      width: "100%",
    };
  }, [exportMode]);

  const option = useMemo(() => {
    const text = "#614a9e";
    const textSecondary = dark ? "#9CA3AF" : "#374151";
    const border = dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.12)";
    const purple = "#6D28D9";

    const nodeNames = new Set();
    (links || []).forEach((l) => {
      if (l?.source) nodeNames.add(l.source);
      if (l?.target) nodeNames.add(l.target);
    });

    const nodes = Array.from(nodeNames).map((name) => ({ name }));

    return {
      backgroundColor: "transparent",

      title: exportMode
        ? { show: false }
        : {
            text: title,
            left: 12,
            top: 12,
            textStyle: {
              fontSize: 14,
              fontWeight: 800,
              color: text,
            },
          },

      toolbox: { show: false },

      tooltip: {
        trigger: "item",
        backgroundColor: dark ? "#303030" : "#FFFFFF",
        borderColor: border,
        textStyle: { color: textSecondary },
        formatter: (p) =>
          p.dataType === "edge"
            ? `<b>${p.data.source}</b> → <b>${p.data.target}</b><br/>${p.data.value} notícias`
            : `<b>${p.name}</b>`,
      },

      series: [
        {
          type: "sankey",

          left: exportMode ? 0 : 12,
          right: exportMode ? 0 : 140,
          top: exportMode ? 0 : 56,
          bottom: exportMode ? 0 : 12,

          nodeWidth: exportMode ? 18 : 16,
          nodeGap: exportMode ? 12 : 10,
          draggable: false,

          data: nodes,
          links,

          emphasis: { focus: "adjacency" },

          label: {
            position: "right",
            align: "left",
            verticalAlign: "middle",
            width: exportMode ? 220 : 140,
            overflow: "truncate",
            color: textSecondary,
            fontWeight: 700,
            fontSize: exportMode ? 13 : 11,
            formatter: (p) => {
              const s = String(p?.name ?? "");
              if (!exportMode) {
                if (s.length <= MAX) return s;
                return s.slice(0, MAX) + "...";
              }
              const maxChars = 26;
              if (s.length <= maxChars) return s;
              return s.slice(0, maxChars) + "...";
            },
          },

          lineStyle: {
            color: "gradient",
            opacity: exportMode ? 0.42 : 0.35,
            curveness: 0.5,
          },

          itemStyle: {
            color: purple,
            borderColor: border,
            borderWidth: 1,
          },
        },
      ],
    };
  }, [links, title, dark, exportMode]);

  useEffect(() => {
    const instance = chartRef.current?.getEchartsInstance?.();
    if (!instance) return;

    const t = setTimeout(() => {
      try {
        instance.resize();
      } catch {}
    }, exportMode ? 60 : 0);

    return () => clearTimeout(t);
  }, [exportMode, links]);

  const handleExportPNG = () => {
    const instance = chartRef.current?.getEchartsInstance?.();
    if (!instance) return;

    const bg = readCssVar("--background-graphic", "#FFFFFF");

    const url = instance.getDataURL({
      type: "png",
      pixelRatio: 2,
      backgroundColor: bg,
      excludeComponents: ["toolbox"],
    });

    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}.png`;
    a.click();
  };

  const exportBtnStyle = {
    position: "absolute",
    top: 10,
    right: 10,
    zIndex: 5,

    height: 34,
    padding: "0 12px",
    borderRadius: 12,
    border: "none",
    background: "var(--bottom-dropdown-color)",
    color: "var(--text-color)",
    fontWeight: 900,
    fontSize: 12,

    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    userSelect: "none",
    transition: "transform .08s ease",
  };

  const exportBtnHoverStyle = {
    boxShadow: "0 6px 14px rgba(97, 74, 158, 0.15)",
    backgroundColor: "#614a9e",
    color: "white",
  };

  const iconStyle = {
    width: 18,
    height: 18,
    display: "grid",
    placeItems: "center",
    borderRadius: 8,
    background:
      "linear-gradient(135deg, var(--icon-gradient-start), var(--icon-gradient-end))",
    color: "var(--color-text-button-action)",
    fontSize: 12,
    lineHeight: 1,
    flex: "0 0 auto",
  };

  return (
    <LoadingBlock loading={loading} label="Carregando protagonismo...">
      <div style={wrapStyle}>
        {!exportMode ? (
          <ExportButton
            onClick={handleExportPNG}
            style={exportBtnStyle}
            hoverStyle={exportBtnHoverStyle}
            iconStyle={iconStyle}
          />
        ) : null}

        <ReactECharts
          ref={chartRef}
          key={`${dark ? "dark" : "light"}-${chartKey}`}
          option={option}
          notMerge={false}
          lazyUpdate={true}
          style={{ height: exportMode ? 560 : CHART_H, width: "100%" }}
        />
      </div>
    </LoadingBlock>
  );
}

function ExportButton({ onClick, style, hoverStyle, iconStyle }) {
  const [hover, setHover] = useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ ...style, ...(hover ? hoverStyle : {}) }}
      title="Exportar PNG"
    >
      <span style={iconStyle}>⭳</span>
      Exportar PNG
    </button>
  );
}
